import type { TFunction } from 'i18next';
import type { FrameworkAction, FrameworkArgument } from '../framework/framework.d';
import {
  getFrameworkNumericArgError,
  isFrameworkArgOptionalNoneDefault,
  isFrameworkNumberArgType,
  shouldPreserveRawFrameworkNumericArgument,
} from './frameworkArgTypes';
import {
  canonicalizeSequenceTupleKeyValue,
  isSequenceTupleArg,
  isTupleArg,
  sequenceTupleKeyMember,
  sequenceTupleMemberValueKey,
  sequenceTupleRowIndices,
  tupleMemberValueKey,
} from './tupleArg';
import { withResolvedMotorVelocityLimits } from './motorVelocityLimits';

/**
 * Whether a single scalar framework arg value (keyed `valueKey` in `argValues`) is valid: empty is
 * only allowed for optional/None-default args, raw numeric expressions are preserved, and otherwise
 * the numeric range/format must pass. Shared by every dialog that edits framework action arguments.
 */
export function scalarFrameworkArgIsValid(arg: FrameworkArgument, valueKey: string, argValues: Record<string, string>, t: TFunction): boolean {
  const rawValue = String(argValues[valueKey] ?? '');
  if (rawValue.length === 0) return isFrameworkArgOptionalNoneDefault(arg);
  if (shouldPreserveRawFrameworkNumericArgument(arg, rawValue)) return true;
  return getFrameworkNumericArgError(arg, rawValue, t) === null;
}

/**
 * Whether every hydrated row of a `Sequence[tuple[...]]` arg is valid: each member must pass scalar
 * validation (e.g. `line_number` within range), and the bounded-integer key member (e.g. `line_number`)
 * must be unique across rows. Zero rows is valid (serializes to an empty list). Shared so the program-
 * start, controller, action-drop, autonomous-routine, and sensor-mapping editors validate identically.
 */
export function sequenceTupleRowsAreValid(seqArg: FrameworkArgument, argValues: Record<string, string>, t: TFunction): boolean {
  const keyMember = sequenceTupleKeyMember(seqArg);
  const seenKeyValues = new Set<string>();
  return sequenceTupleRowIndices(seqArg.name, argValues).every((rowIndex) => {
    const membersValid = (seqArg.members || []).every((member) =>
      scalarFrameworkArgIsValid(member as FrameworkArgument, sequenceTupleMemberValueKey(seqArg.name, rowIndex, member.name), argValues, t)
    );
    if (!membersValid) return false;
    if (keyMember) {
      const keyValue = String(argValues[sequenceTupleMemberValueKey(seqArg.name, rowIndex, keyMember.name)] ?? '').trim();
      if (keyValue) {
        // Canonicalize so equivalent spellings (`1`, `1.0`, `01`) count as the same target line.
        const canonicalKey = canonicalizeSequenceTupleKeyValue(keyMember, keyValue);
        if (seenKeyValues.has(canonicalKey)) return false;
        seenKeyValues.add(canonicalKey);
      }
    }
    return true;
  });
}

export function frameworkActionArgsAreValid(actionDef: FrameworkAction | null | undefined, argValues: Record<string, string>, t: TFunction): boolean {
  const effectiveActionDef = withResolvedMotorVelocityLimits(actionDef, argValues);
  return (effectiveActionDef?.arguments ?? []).every((arg) => {
    const frameworkArg = arg as FrameworkArgument;
    if (isSequenceTupleArg(frameworkArg)) {
      return sequenceTupleRowsAreValid(frameworkArg, argValues, t);
    }
    if (isTupleArg(frameworkArg)) {
      return (frameworkArg.members || []).every((member) => {
        const memberArg = member as FrameworkArgument;
        const memberKey = tupleMemberValueKey(frameworkArg.name, member.name);
        return !(memberKey in argValues) || scalarFrameworkArgIsValid(memberArg, memberKey, argValues, t);
      });
    }
    const rawValue = String(argValues[frameworkArg.name] ?? '');
    if (rawValue.length === 0 && frameworkArg.defaultValue !== undefined && !isFrameworkNumberArgType(frameworkArg.type ?? '')) return true;
    return scalarFrameworkArgIsValid(frameworkArg, frameworkArg.name, argValues, t);
  });
}
